import { useState, useEffect, useCallback, useRef } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    IconButton,
    Chip,
    Tooltip,
    Divider,
} from "@mui/material";
import {
    Close,
    MusicNote,
    YouTube,
    ArrowBack,
    Bolt as FlashIcon,
    FastForward as FastForwardIcon,
    FilterList,
    Add,
} from "@mui/icons-material";
import { getImageUrl } from "../../utils/imageHelper";
import { useSnackbar } from 'notistack';
import io from 'socket.io-client';
import {
    DndContext, 
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableSetSection } from './PrivatePlaylistTab/SetSection';
import AddSongModal from './PrivatePlaylistTab/AddSongModal';
import CreatePlaylistModal from './PrivatePlaylistTab/CreatePlaylistModal';

const PrivatePlaylistTab = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [privatePlaylists, setPrivatePlaylists] = useState([]);
    const [selectedPlaylist, setSelectedPlaylist] = useState(null);
    const [songs, setSongs] = useState([]);
    const [sets, setSets] = useState([]);
    const [selectedSong, setSelectedSong] = useState(null);
    const [openSongModal, setOpenSongModal] = useState(false);
    const [openCreatePlaylistModal, setOpenCreatePlaylistModal] = useState(false);
    const [openAddSongModal, setOpenAddSongModal] = useState(false);
    const [targetSetIdForAdd, setTargetSetIdForAdd] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingSongs, setLoadingSongs] = useState(false);
    const [collapsedSets, setCollapsedSets] = useState(new Set());

    // Flash Quiz states
    const [activeFlashSession, setActiveFlashSession] = useState(null);
    const [loadingFlashSession, setLoadingFlashSession] = useState(false);
    const [pushingQuestion, setPushingQuestion] = useState(null);
    const [forcePushingQuestion, setForcePushingQuestion] = useState(null);
    const [stoppingQuestion, setStoppingQuestion] = useState(false);
    const [songQuestions, setSongQuestions] = useState({});
    const [currentTime, setCurrentTime] = useState(Date.now());
    const [completedSongIds, setCompletedSongIds] = useState(() => new Set());
    const [pushedAtMap, setPushedAtMap] = useState({});
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [pendingSong, setPendingSong] = useState(null);
    const socketRef = useRef(null);
    const lastCurrentSongIdRef = useRef(null);
    const recentlyStoppedRef = useRef(false);

    // Initialize drag and drop sensors at the top level (hooks must be called unconditionally)
    const setSensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Require 8px movement before activating drag for sets
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const getTotalDuration = (songs = []) => {
        let totalSeconds = 0;

        songs.forEach((song) => {
            if (!song?.duration) return;
            const parts = song.duration.split(":").map(Number);
            if (parts.length === 2) {
                const [min, sec] = parts;
                totalSeconds += (isNaN(min) ? 0 : min) * 60 + (isNaN(sec) ? 0 : sec);
            } else if (parts.length === 3) {
                const [hr, min, sec] = parts;
                totalSeconds += (isNaN(hr) ? 0 : hr) * 3600 + (isNaN(min) ? 0 : min) * 60 + (isNaN(sec) ? 0 : sec);
            }
        });

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        if (hours > 0) {
            return `${hours} hr ${minutes} min ${seconds} sec`;
        } else {
            return `${minutes} min ${seconds} sec`;
        }
    };

    const fetchPrivatePlaylists = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/playlist/private`
            );
            if (response.ok) {
                const data = await response.json();
                const playlistsWithDuration = await Promise.all(
                    data.map(async (pl) => {
                        try {
                            const songsRes = await fetch(
                                `${process.env.REACT_APP_API_URL}/api/playlist-songs/playlist/${pl.playlist_master_id}/songs`
                            );
                            const songsData = songsRes.ok ? await songsRes.json() : [];
                            return { ...pl, total_duration_text: getTotalDuration(songsData) };
                        } catch {
                            return { ...pl, total_duration_text: "N/A" };
                        }
                    })
                );
                setPrivatePlaylists(playlistsWithDuration);
            }
        } catch (error) {
            console.error("Error fetching private playlists:", error);
            enqueueSnackbar("Failed to load private playlists", { variant: "error" });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    const fetchPlaylistSongs = useCallback(async (playlistId) => {
        setLoadingSongs(true);
        try {
            // Fetch sets with songs
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/playlist-sets/playlist/${playlistId}`
            );
            if (response.ok) {
                const setsData = await response.json();
                
                // If no sets exist, create Set 1 and assign all songs to it
                if (setsData.length === 0) {
                    // First, fetch all songs to see what we have
                    const songsResponse = await fetch(
                        `${process.env.REACT_APP_API_URL}/api/playlist-songs/playlist/${playlistId}/songs`
                    );
                    
                    if (songsResponse.ok) {
                        const songsData = await songsResponse.json();
                        
                        // Create Set 1
                        const createSetResponse = await fetch(
                            `${process.env.REACT_APP_API_URL}/api/playlist-sets`,
                            {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    playlist_master_id: playlistId,
                                    set_name: 'Set 1',
                                    set_order: 0,
                                }),
                            }
                        );
                        
                        if (createSetResponse.ok) {
                            const newSet = await createSetResponse.json();
                            
                            // If there are songs, assign them all to Set 1
                            if (songsData.length > 0) {
                                const updates = songsData
                                    .filter(song => song.playlist_songs_id) // Only songs with playlist_songs_id
                                    .map((song, index) => ({
                                        playlist_songs_id: song.playlist_songs_id,
                                        set_id: newSet.set_id,
                                        display_order: index,
                                    }));
                                
                                if (updates.length > 0) {
                                    await fetch(
                                        `${process.env.REACT_APP_API_URL}/api/playlist-songs-order/bulk`,
                                        {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ updates }),
                                        }
                                    );
                                }
                            }
                            
                            // Refetch sets with songs
                            const refetchResponse = await fetch(
                                `${process.env.REACT_APP_API_URL}/api/playlist-sets/playlist/${playlistId}`
                            );
                            if (refetchResponse.ok) {
                                const refetchedSets = await refetchResponse.json();
                                setSets(refetchedSets);
                                const allSongs = refetchedSets.flatMap(set => set.songs || []);
                                setSongs(allSongs);
                            } else {
                                // Fallback: show Set 1 with songs
                                setSets([{ ...newSet, songs: songsData }]);
                                setSongs(songsData);
                            }
                        } else {
                            // If set creation fails, show songs without sets
                            setSets([]);
                            setSongs(songsData);
                        }
                    } else {
                        // No songs endpoint, create empty Set 1
                        const createSetResponse = await fetch(
                            `${process.env.REACT_APP_API_URL}/api/playlist-sets`,
                            {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    playlist_master_id: playlistId,
                                    set_name: 'Set 1',
                                    set_order: 0,
                                }),
                            }
                        );
                        
                        if (createSetResponse.ok) {
                            const newSet = await createSetResponse.json();
                            setSets([{ ...newSet, songs: [] }]);
                            setSongs([]);
                        } else {
                            setSets([]);
                            setSongs([]);
                        }
                    }
                } else {
                    // Sets exist, use them
                    setSets(setsData);
                    // Flatten all songs for compatibility with existing code
                    const allSongs = setsData.flatMap(set => set.songs || []);
                    setSongs(allSongs);
                }
            } else {
                // Fallback: if sets endpoint fails, try old endpoint
                const fallbackResponse = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/playlist-songs/playlist/${playlistId}/songs`
                );
                if (fallbackResponse.ok) {
                    const data = await fallbackResponse.json();
                    setSongs(data);
                    setSets([]);
                }
            }
        } catch (error) {
            console.error("Error fetching playlist songs:", error);
            enqueueSnackbar("Failed to load songs", { variant: "error" });
        } finally {
            setLoadingSongs(false);
        }
    }, [enqueueSnackbar]);

    const handlePlaylistClick = async (playlist) => {
        setSelectedPlaylist(playlist);
        await fetchPlaylistSongs(playlist.playlist_master_id);
    };

    const handleBackToPlaylists = () => {
        setSelectedPlaylist(null);
        setSongs([]);
        setSets([]);
        setSelectedSong(null);
    };

    // Sets management functions
    const handleCreateSet = async () => {
        if (!selectedPlaylist) return;
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/playlist-sets`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    playlist_master_id: selectedPlaylist.playlist_master_id,
                }),
            });
            if (response.ok) {
                await fetchPlaylistSongs(selectedPlaylist.playlist_master_id);
                enqueueSnackbar('Set created successfully', { variant: 'success' });
            } else {
                throw new Error('Failed to create set');
            }
        } catch (error) {
            console.error('Error creating set:', error);
            enqueueSnackbar('Failed to create set', { variant: 'error' });
        }
    };

    const handleEditSetName = async (setId, newName) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/playlist-sets/${setId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ set_name: newName }),
            });
            if (response.ok) {
                await fetchPlaylistSongs(selectedPlaylist.playlist_master_id);
                enqueueSnackbar('Set name updated', { variant: 'success' });
            } else {
                throw new Error('Failed to update set name');
            }
        } catch (error) {
            console.error('Error updating set name:', error);
            enqueueSnackbar('Failed to update set name', { variant: 'error' });
        }
    };

    const handleDeleteSet = async (setId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/playlist-sets/${setId}`, {
                method: 'DELETE',
            });
            if (response.ok) {
                await fetchPlaylistSongs(selectedPlaylist.playlist_master_id);
                enqueueSnackbar('Set deleted successfully', { variant: 'success' });
            } else {
                const error = await response.json();
                throw new Error(error.error || 'Failed to delete set');
            }
        } catch (error) {
            console.error('Error deleting set:', error);
            enqueueSnackbar(error.message || 'Failed to delete set', { variant: 'error' });
        }
    };

    const handleSongsReorder = async (setId, reorderedSongs) => {
        // Optimistically update local state
        setSets(prevSets => prevSets.map(set => 
            set.set_id === setId 
                ? { ...set, songs: reorderedSongs }
                : set
        ));
        setSongs(prevSongs => {
            const otherSongs = prevSongs.filter(s => s.set_id !== setId);
            return [...otherSongs, ...reorderedSongs];
        });

        try {
            const updates = reorderedSongs.map((song, index) => ({
                playlist_songs_id: song.playlist_songs_id,
                set_id: setId,
                display_order: index,
            }));

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/playlist-songs-order/bulk`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates }),
            });

            if (!response.ok) {
                throw new Error('Failed to update song order');
            }
        } catch (error) {
            console.error('Error reordering songs:', error);
            enqueueSnackbar('Failed to update song order', { variant: 'error' });
            // Revert on error
            await fetchPlaylistSongs(selectedPlaylist.playlist_master_id);
        }
    };

    // Handle song drag and drop (within set and between sets)
    const handleSongDragEnd = async (event) => {
        const { active, over } = event;
        
        if (!over || !active) return;

        const activeId = active.id;
        const overId = over.id;

        // Find the song being dragged
        let draggedSong = null;
        let sourceSetId = null;
        for (const set of sets) {
            const song = set.songs?.find(s => s.playlist_songs_id === activeId);
            if (song) {
                draggedSong = song;
                sourceSetId = set.set_id;
                break;
            }
        }

        if (!draggedSong) return;

        // Check if dropping on another set (set droppable zone)
        if (typeof overId === 'string' && overId.startsWith('set-')) {
            const targetSetId = parseInt(overId.replace('set-', ''));
            if (targetSetId !== sourceSetId) {
                // Moving song to different set
                try {
                    const targetSet = sets.find(s => s.set_id === targetSetId);
                    const newOrder = targetSet?.songs?.length || 0;

                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/api/playlist-songs-order/bulk`,
                        {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                updates: [{
                                    playlist_songs_id: draggedSong.playlist_songs_id,
                                    set_id: targetSetId,
                                    display_order: newOrder,
                                }],
                            }),
                        }
                    );

                    if (response.ok) {
                        // Optimistically update local state
                        setSets(prevSets => prevSets.map(set => {
                            if (set.set_id === sourceSetId) {
                                return { ...set, songs: set.songs?.filter(s => s.playlist_songs_id !== activeId) || [] };
                            }
                            if (set.set_id === targetSetId) {
                                return { ...set, songs: [...(set.songs || []), { ...draggedSong, set_id: targetSetId, display_order: newOrder }] };
                            }
                            return set;
                        }));
                        setSongs(prevSongs => {
                            const filtered = prevSongs.filter(s => s.playlist_songs_id !== activeId);
                            return [...filtered, { ...draggedSong, set_id: targetSetId, display_order: newOrder }];
                        });
                        enqueueSnackbar('Song moved to ' + (targetSet?.set_name || 'Set'), { variant: 'success' });
                    } else {
                        throw new Error('Failed to move song');
                    }
                } catch (error) {
                    console.error('Error moving song between sets:', error);
                    enqueueSnackbar('Failed to move song', { variant: 'error' });
                }
            }
            return;
        }

        // Check if dropping on another song (reordering within same set or different set)
        const targetSong = sets
            .flatMap(set => set.songs || [])
            .find(s => s.playlist_songs_id === overId);

        if (targetSong) {
            const targetSetId = targetSong.set_id;
            
            // Find source and target sets
            const sourceSet = sets.find(s => s.set_id === sourceSetId);
            const targetSet = sets.find(s => s.set_id === targetSetId);

            if (!sourceSet || !targetSet) return;

            const sourceSongs = [...(sourceSet.songs || [])];
            const targetSongs = [...(targetSet.songs || [])];

            if (sourceSetId === targetSetId) {
                // Reordering within same set
                const oldIndex = sourceSongs.findIndex(s => s.playlist_songs_id === activeId);
                const newIndex = sourceSongs.findIndex(s => s.playlist_songs_id === overId);

                if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
                    const reorderedSongs = arrayMove(sourceSongs, oldIndex, newIndex);
                    handleSongsReorder(sourceSetId, reorderedSongs);
                }
            } else {
                // Moving between sets
                const sourceIndex = sourceSongs.findIndex(s => s.playlist_songs_id === activeId);
                const targetIndex = targetSongs.findIndex(s => s.playlist_songs_id === overId);

                if (sourceIndex !== -1 && targetIndex !== -1) {
                    // Remove from source set
                    const [movedSong] = sourceSongs.splice(sourceIndex, 1);
                    // Insert into target set at target position
                    targetSongs.splice(targetIndex, 0, { ...movedSong, set_id: targetSetId });

                    // Update both sets
                    const updates = [
                        ...sourceSongs.map((song, index) => ({
                            playlist_songs_id: song.playlist_songs_id,
                            set_id: sourceSetId,
                            display_order: index,
                        })),
                        ...targetSongs.map((song, index) => ({
                            playlist_songs_id: song.playlist_songs_id,
                            set_id: targetSetId,
                            display_order: index,
                        })),
                    ];

                    try {
                        const response = await fetch(
                            `${process.env.REACT_APP_API_URL}/api/playlist-songs-order/bulk`,
                            {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ updates }),
                            }
                        );

                        if (response.ok) {
                            // Optimistically update local state
                            setSets(prevSets => prevSets.map(set => {
                                if (set.set_id === sourceSetId) {
                                    return { ...set, songs: sourceSongs };
                                }
                                if (set.set_id === targetSetId) {
                                    return { ...set, songs: targetSongs };
                                }
                                return set;
                            }));
                            setSongs(prevSongs => {
                                const filtered = prevSongs.filter(s => 
                                    !(s.set_id === sourceSetId || s.set_id === targetSetId)
                                );
                                return [...filtered, ...sourceSongs, ...targetSongs];
                            });
                            enqueueSnackbar(`Song moved to ${targetSet.set_name}`, { variant: 'success' });
                        } else {
                            throw new Error('Failed to move song');
                        }
                    } catch (error) {
                        console.error('Error moving song between sets:', error);
                        enqueueSnackbar('Failed to move song', { variant: 'error' });
                    }
                }
            }
        }
    };

    const handleAddSongs = async (selectedSongs, targetSetId) => {
        if (!selectedPlaylist || !targetSetId) return;

        try {
            // Get the set to find its ID
            const targetSet = sets.find(s => s.set_id === targetSetId);
            if (!targetSet) {
                throw new Error('Set not found');
            }

            // Add each song to the set
            const promises = selectedSongs.map(async (song, index) => {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/playlist-songs`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        playlist_master_id: selectedPlaylist.playlist_master_id,
                        songs_master_id: song.songs_master_id,
                        set_id: targetSetId,
                        display_order: (targetSet.songs?.length || 0) + index,
                    }),
                });
                if (!response.ok) {
                    throw new Error(`Failed to add song ${song.name}`);
                }
            });

            await Promise.all(promises);
            await fetchPlaylistSongs(selectedPlaylist.playlist_master_id);
            enqueueSnackbar(`${selectedSongs.length} song(s) added successfully`, { variant: 'success' });
        } catch (error) {
            console.error('Error adding songs:', error);
            enqueueSnackbar(error.message || 'Failed to add songs', { variant: 'error' });
        }
    };

    const handleOpenAddSongModal = (setId = null) => {
        setTargetSetIdForAdd(setId);
        setOpenAddSongModal(true);
    };

    const handleToggleSetCollapse = (setId) => {
        setCollapsedSets(prev => {
            const newSet = new Set(prev);
            if (newSet.has(setId)) {
                newSet.delete(setId);
            } else {
                newSet.add(setId);
            }
            return newSet;
        });
    };

    const handleCreatePlaylist = async (newPlaylist) => {
        await fetchPrivatePlaylists();
        enqueueSnackbar('Playlist created successfully', { variant: 'success' });
    };

    // Fetch active flash quiz session
    const fetchActiveFlashSession = useCallback(async () => {
        try {
            setLoadingFlashSession(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/live-quiz/flash/active`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.sessions && data.sessions.length > 0) {
                    setActiveFlashSession(data.sessions[0]);
                } else {
                    setActiveFlashSession(null);
                }
            }
        } catch (error) {
            console.error('Error fetching active flash session:', error);
        } finally {
            setLoadingFlashSession(false);
        }
    }, []);

    // Fetch questions for a song
    const fetchSongQuestions = useCallback(async (songId, config) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/quiz/song/${songId}?` +
                `difficulty_id=${config?.difficulty_id || 7}&` +
                `questions_per_song=${config?.questions_per_song || 3}`
            );

            if (!response.ok) {
                return null;
            }

            const data = await response.json();
            return data.questions || [];
        } catch (error) {
            console.error(`Error fetching questions for song ${songId}:`, error);
            return null;
        }
    }, []);

    // Push song question to flash quiz
    const pushFlashQuestion = async (songId) => {
        if (!activeFlashSession) {
            enqueueSnackbar('No active flash quiz session', { variant: 'warning' });
            return;
        }

        try {
            setPushingQuestion(songId);
            const rawRapid = activeFlashSession?.rapid_config;
            const parsedRapid = typeof rawRapid === 'string' ? (() => { try { return JSON.parse(rawRapid); } catch { return {}; } })() : (rawRapid || {});
            const sessionDelay = typeof parsedRapid.delay_seconds === 'number' ? parsedRapid.delay_seconds : 10;
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/live-quiz/flash/sessions/${activeFlashSession.session_id}/push-song/${songId}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                    },
                    body: JSON.stringify({
                        delay_seconds: sessionDelay
                    })
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to push question');
            }

            await response.json();
            enqueueSnackbar(`Question pushed! Delay per config: ${sessionDelay}s`, { variant: 'success' });
            setPushedAtMap(prev => ({ ...prev, [songId]: new Date().toISOString() }));
        } catch (error) {
            console.error('Error pushing flash question:', error);
            enqueueSnackbar(error.message || 'Failed to push question', { variant: 'error' });
        } finally {
            setPushingQuestion(null);
        }
    };

    // Stop current question
    const handleStopQuestion = async () => {
        if (!activeFlashSession) {
            enqueueSnackbar('No active flash quiz session', { variant: 'warning' });
            return;
        }

        try {
            recentlyStoppedRef.current = true;
            setStoppingQuestion(true);
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/live-quiz/flash/sessions/${activeFlashSession.session_id}/stop-question`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                    }
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to stop question');
            }

            enqueueSnackbar('Question stopped successfully', { variant: 'success' });
        } catch (error) {
            console.error('Error stopping question:', error);
            enqueueSnackbar(error.message || 'Failed to stop question', { variant: 'error' });
        } finally {
            setStoppingQuestion(false);
        }
    };

    // Get current timer state
    const getTimerState = useCallback(() => {
        if (!activeFlashSession?.rapid_config) return null;

        try {
            const rapidConfig = typeof activeFlashSession.rapid_config === 'string'
                ? JSON.parse(activeFlashSession.rapid_config)
                : activeFlashSession.rapid_config;

            const pushedAt = rapidConfig.question_pushed_at;
            const deliveryTime = rapidConfig.question_delivery_time;
            const songTimerSeconds = rapidConfig.song_timer_seconds || 60;
            const currentSongId = rapidConfig.current_song_id;

            if (!pushedAt || !deliveryTime || !currentSongId) return null;

            const now = currentTime;
            const delivery = new Date(deliveryTime).getTime();
            const quizletEnd = delivery + (songTimerSeconds * 1000);

            // Check if we're in delay phase
            if (now < delivery) {
                const remaining = Math.max(0, Math.ceil((delivery - now) / 1000));
                return {
                    phase: 'delay',
                    remainingSeconds: remaining,
                    currentSongId,
                    songTimerSeconds
                };
            }

            // Check if we're in quizlet timer phase
            if (now < quizletEnd) {
                const remaining = Math.max(0, Math.ceil((quizletEnd - now) / 1000));
                return {
                    phase: 'quizlet',
                    remainingSeconds: remaining,
                    currentSongId,
                    songTimerSeconds
                };
            }

            // Timer ended
            return null;
        } catch {
            return null;
        }
    }, [activeFlashSession, currentTime]);

    // Format timer display
    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Get button state for a song
    const getButtonState = (song) => {
        const timerState = getTimerState();
        const isCurrentSong = timerState && timerState.currentSongId === song.songs_master_id;
        const isCompleted = completedSongIds.has(song.songs_master_id);

        if (!timerState) {
            return { type: 'push', disabled: isCompleted };
        }

        if (isCurrentSong) {
            return {
                type: 'stop',
                color: timerState.phase === 'delay' ? 'success' : 'error',
                disabled: stoppingQuestion,
                timer: formatTimer(timerState.remainingSeconds),
                phase: timerState.phase
            };
        }

        return { type: 'push', disabled: isCompleted };
    };

    const hasQuestions = (songId) => {
        const questions = songQuestions[songId];
        return questions && questions.length > 0;
    };

    const handlePushClick = (song) => {
        const timerState = getTimerState();

        // Check if another song is currently running
        if (timerState && timerState.currentSongId !== song.songs_master_id) {
            setPendingSong(song);
            setConfirmDialogOpen(true);
            return;
        }

        pushFlashQuestion(song.songs_master_id);
    };

    const handlePushConfirm = async () => {
        if (!pendingSong) return;
        await pushFlashQuestion(pendingSong.songs_master_id);
        setPendingSong(null);
        setConfirmDialogOpen(false);
    };

    const handleForceClick = async (songId) => {
        if (!hasQuestions(songId)) {
            enqueueSnackbar('No questions available for this song', { variant: 'warning' });
            return;
        }
        try {
            setForcePushingQuestion(songId);
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/quiz/force-push/${songId}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
                    },
                }
            );
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Failed to force push question');
            }
            enqueueSnackbar(data.message || 'Question pushed to logged-in users', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(error.message || 'Failed to force push', { variant: 'error' });
        } finally {
            setForcePushingQuestion(null);
        }
    };

    const getCurrentRunningSongName = useCallback(() => {
        const timerState = getTimerState();
        if (!timerState) return null;

        const song = songs.find(s => s.songs_master_id === timerState.currentSongId);
        return song ? song.name : 'Another song';
    }, [getTimerState, songs]);

    const handleSongClick = async (song) => {
        try {
            // Fetch full song details
            const songRes = await fetch(
                `${process.env.REACT_APP_API_URL}/api/songs/${song.songs_master_id}`
            );
            const songData = await songRes.json();

            // Fetch URLs and files
            const urlsRes = await fetch(
                `${process.env.REACT_APP_API_URL}/api/song-urls/urls?songs_master_id=${song.songs_master_id}`
            );
            const urlsData = await urlsRes.json();

            const filesRes = await fetch(
                `${process.env.REACT_APP_API_URL}/api/song-files/song/${song.songs_master_id}`
            );
            const filesData = await filesRes.json();

            setSelectedSong({
                ...songData,
                urls: urlsData,
                files: filesData
            });
            setOpenSongModal(true);

            // Fetch flash session when modal opens
            fetchActiveFlashSession();
        } catch (error) {
            console.error("Error loading song info:", error);
            enqueueSnackbar("Failed to load song details", { variant: "error" });
        }
    };

    // Load questions for all songs (for Push when session active, for Force always)
    useEffect(() => {
        if (songs.length === 0) return;

        const config = activeFlashSession?.quiz_config
            ? (typeof activeFlashSession.quiz_config === 'string'
                ? JSON.parse(activeFlashSession.quiz_config)
                : activeFlashSession.quiz_config)
            : { difficulty_id: 7, questions_per_song: 1 };

        const loadQuestions = async () => {
            // Fetch all questions in parallel instead of sequentially
            const questionPromises = songs.map(song =>
                fetchSongQuestions(song.songs_master_id, config).then(questions => ({
                    songId: song.songs_master_id,
                    questions
                }))
            );
            
            const results = await Promise.all(questionPromises);
            
            const questionsMap = {};
            results.forEach(({ songId, questions }) => {
                questionsMap[songId] = questions;
            });
            setSongQuestions(questionsMap);
        };

        loadQuestions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFlashSession?.session_id, activeFlashSession?.quiz_config, songs.map(s => s.songs_master_id).join(','), fetchSongQuestions]);

    // Fetch active session on mount and when songs change
    useEffect(() => {
        if (songs.length > 0) {
            fetchActiveFlashSession();
        }
    }, [songs.length, fetchActiveFlashSession]);

    // WebSocket connection for real-time session updates
    useEffect(() => {
        if (songs.length === 0) return;

        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const socket = io(baseUrl, { transports: ['websocket'] });
        socketRef.current = socket;

        socket.on('connect', () => {
            socket.emit('flash:join-global');
        });

        socket.on('flash:question_pushed', async (payload) => {
            if (payload && payload.session_id) {
                await fetchActiveFlashSession();
                try {
                    if (payload.current_song?.songs_master_id && payload.pushed_at) {
                        setPushedAtMap(old => ({
                            ...old,
                            [payload.current_song.songs_master_id]: payload.pushed_at
                        }));
                    }
                } catch { }
            }
        });

        socket.on('flash:question_stopped', async (payload) => {
            if (payload && payload.session_id) {
                recentlyStoppedRef.current = true;
                await fetchActiveFlashSession();
            }
        });

        socket.on('flash:session_ended', async (payload) => {
            if (payload && payload.session_id) {
                setActiveFlashSession(prev => {
                    if (prev && prev.session_id === payload.session_id) {
                        return null;
                    }
                    return prev;
                });
                try {
                    localStorage.removeItem(`flash_completed_${payload.session_id}`);
                } catch { }
                setCompletedSongIds(new Set());
            }
        });

        socket.on('flash:activated', async (payload) => {
            if (payload && payload.session_id) {
                await fetchActiveFlashSession();
            }
        });

        return () => {
            if (socketRef.current) {
                socketRef.current.emit('flash:leave-global');
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
    }, [songs.length, fetchActiveFlashSession]);

    // Update current time every second for timer display
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(Date.now());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Track when a song's timer completes
    useEffect(() => {
        const timerState = getTimerState();
        const previousSongId = lastCurrentSongIdRef.current;
        if (timerState && timerState.currentSongId) {
            lastCurrentSongIdRef.current = timerState.currentSongId;
        } else {
            if (previousSongId && !recentlyStoppedRef.current) {
                setCompletedSongIds(prev => {
                    const updated = new Set(prev);
                    updated.add(previousSongId);
                    return updated;
                });
            }
            recentlyStoppedRef.current = false;
            lastCurrentSongIdRef.current = null;
        }
    }, [getTimerState]);

    // Auto-close confirmation dialog when timer ends
    useEffect(() => {
        const timerState = getTimerState();
        if (!timerState && confirmDialogOpen) {
            setConfirmDialogOpen(false);
            setPendingSong(null);
        }
    }, [getTimerState, confirmDialogOpen]);

    // Load persisted "completed for session" state on session change
    useEffect(() => {
        try {
            const sessionId = activeFlashSession?.session_id;
            if (!sessionId) {
                setCompletedSongIds(new Set());
                return;
            }
            const raw = localStorage.getItem(`flash_completed_${sessionId}`);
            if (raw) {
                const arr = JSON.parse(raw);
                if (Array.isArray(arr)) {
                    setCompletedSongIds(new Set(arr));
                }
            } else {
                setCompletedSongIds(new Set());
            }
        } catch {
            setCompletedSongIds(new Set());
        }
    }, [activeFlashSession?.session_id]);

    // Persist "completed for session" state whenever it changes
    useEffect(() => {
        try {
            const sessionId = activeFlashSession?.session_id;
            if (!sessionId) return;
            localStorage.setItem(`flash_completed_${sessionId}`, JSON.stringify(Array.from(completedSongIds)));
        } catch { }
    }, [completedSongIds, activeFlashSession?.session_id]);

    // Load persisted "last pushed" times on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem('flash_last_pushed_map');
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed && typeof parsed === 'object') {
                    setPushedAtMap(parsed);
                }
            }
        } catch { }
    }, []);

    // Persist "last pushed" times whenever updated
    useEffect(() => {
        try {
            localStorage.setItem('flash_last_pushed_map', JSON.stringify(pushedAtMap));
        } catch { }
    }, [pushedAtMap]);

    // Load last pushed timestamps from server for current list
    useEffect(() => {
        const fetchMap = async () => {
            try {
                if (!Array.isArray(songs) || songs.length === 0) return;
                const ids = songs.map(s => s?.songs_master_id).filter(Boolean);
                if (ids.length === 0) return;
                const base = process.env.REACT_APP_API_URL || 'http://localhost:3001';
                const resp = await fetch(`${base}/api/songs/last-pushed/map?ids=${ids.join(',')}`);
                if (!resp.ok) return;
                const data = await resp.json();
                if (data?.map) {
                    setPushedAtMap(prev => ({ ...prev, ...data.map }));
                }
            } catch (e) {
                console.warn('last-pushed fetch failed:', e?.message);
            }
        };
        fetchMap();
    }, [songs]);

    useEffect(() => {
        fetchPrivatePlaylists();
    }, [fetchPrivatePlaylists]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Show songs if a playlist is selected
    const normalizeSetId = (id) => {
        if (typeof id === 'string' && id.startsWith('set-')) {
            return Number(id.slice(4));
        }
        return id;
    };

    if (selectedPlaylist) {
        const handleSetsReorder = async (event) => {
            const { active, over } = event;
            if (over) {
                const activeId = normalizeSetId(active.id);
                const overId = normalizeSetId(over.id);

                if (activeId === overId) return;

                const oldIndex = sets.findIndex(s => s.set_id === activeId);
                const newIndex = sets.findIndex(s => s.set_id === overId);
                
                if (oldIndex === -1 || newIndex === -1) return;
                
                const newSets = arrayMove(sets, oldIndex, newIndex);

                // Optimistically update local state immediately
                setSets(newSets);

                // Update set orders
                const setOrders = newSets.map((set, index) => ({
                    set_id: set.set_id,
                    set_order: index,
                }));

                try {
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/playlist-sets/reorder`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            playlist_master_id: selectedPlaylist.playlist_master_id,
                            setOrders,
                        }),
                    });
                    if (response.ok) {
                        enqueueSnackbar('Sets reordered successfully', { variant: 'success' });
                    } else {
                        throw new Error('Failed to reorder sets');
                    }
                } catch (error) {
                    console.error('Error reordering sets:', error);
                    enqueueSnackbar('Failed to reorder sets', { variant: 'error' });
                    // Revert on error
                    await fetchPlaylistSongs(selectedPlaylist.playlist_master_id);
                }
            }
        };

        return (
            <Box sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <IconButton onClick={handleBackToPlaylists} sx={{ color: "white" }}>
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="h6" sx={{ color: "white" }}>
                            {selectedPlaylist.title} - Sets ({sets.length})
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={handleCreateSet}
                            sx={{
                                borderColor: '#4caf50',
                                color: '#4caf50',
                                '&:hover': { borderColor: '#45a049', bgcolor: 'rgba(76, 175, 80, 0.1)' },
                            }}
                        >
                            Create Set
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => handleOpenAddSongModal(null)}
                            sx={{
                                bgcolor: '#4caf50',
                                '&:hover': { bgcolor: '#45a049' },
                            }}
                        >
                            Add Song
                        </Button>
                    </Box>
                </Box>

                {loadingSongs ? (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : sets.length === 0 ? (
                    <Box sx={{ textAlign: "center", mt: 4 }}>
                        <Typography sx={{ color: "white", mb: 2 }}>
                            Loading sets...
                        </Typography>
                    </Box>
                ) : (
                    <DndContext
                        sensors={setSensors}
                        collisionDetection={closestCenter}
                        onDragStart={(event) => {
                            // Track what's being dragged
                            const activeId = normalizeSetId(event.active.id);
                            const isSetDrag = sets.some(s => s.set_id === activeId);
                            if (isSetDrag) {
                                // Auto-collapse the set being dragged
                                const draggedSet = sets.find(s => s.set_id === activeId);
                                if (draggedSet && !collapsedSets.has(draggedSet.set_id)) {
                                    handleToggleSetCollapse(draggedSet.set_id);
                                }
                            }
                        }}
                        onDragEnd={(event) => {
                            // Check if dragging a set or a song
                            const activeId = normalizeSetId(event.active.id);
                            const isSetDrag = sets.some(s => s.set_id === activeId);
                            
                            if (isSetDrag) {
                                handleSetsReorder(event);
                            } else {
                                // Song drag is handled within each set's own DndContext
                                // But we need to handle cross-set dragging here
                                handleSongDragEnd(event);
                            }
                        }}
                    >
                        <SortableContext
                            items={sets.map(s => s.set_id)}
                            strategy={verticalListSortingStrategy}
                        >
                            {sets.map((set) => (
                                <SortableSetSection
                                    key={set.set_id}
                                    set={set}
                                    songs={set.songs || []}
                                    onAddSong={() => handleOpenAddSongModal(set.set_id)}
                                    onEditSetName={handleEditSetName}
                                    onDeleteSet={handleDeleteSet}
                                    onSongClick={handleSongClick}
                                    getButtonState={getButtonState}
                                    hasQuestions={hasQuestions}
                                    handlePushClick={handlePushClick}
                                    handleStopQuestion={handleStopQuestion}
                                    pushingQuestion={pushingQuestion}
                                    stoppingQuestion={stoppingQuestion}
                                    activeFlashSession={activeFlashSession}
                                    loadingFlashSession={loadingFlashSession}
                                    isCollapsed={collapsedSets.has(set.set_id)}
                                    onToggleCollapse={() => handleToggleSetCollapse(set.set_id)}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                )}

                {/* Song Details Modal */}
                <Dialog
                    open={openSongModal}
                    onClose={() => setOpenSongModal(false)}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            bgcolor: "#1e1e1e",
                            color: "white",
                        },
                    }}
                >
                    <DialogTitle sx={{ bgcolor: "#2c2c2c", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            {selectedSong?.image && (
                                <img
                                    src={getImageUrl(selectedSong.image)}
                                    alt={selectedSong.name}
                                    style={{ width: 60, height: 60, borderRadius: 4, objectFit: "cover" }}
                                />
                            )}
                            <Box>
                                <Typography variant="h6">
                                    {selectedSong?.name} {selectedSong?.release_year && `(${selectedSong.release_year})`}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {selectedSong?.artist || "Unknown Artist"}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={() => setOpenSongModal(false)} sx={{ color: "white" }}>
                            <Close />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent dividers sx={{ bgcolor: "#121212" }}>
                        {/* Song Details */}
                        <Box mb={2}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                Artist: <Typography component="span" color="text.secondary">{selectedSong?.artist || "N/A"}</Typography>
                            </Typography>
                            {selectedSong?.album && (
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>
                                    Album: <Typography component="span" color="text.secondary">{selectedSong.album}</Typography>
                                </Typography>
                            )}
                            {selectedSong?.duration && (
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>
                                    Duration: <Typography component="span" color="text.secondary">{selectedSong.duration}</Typography>
                                </Typography>
                            )}
                        </Box>

                        <Divider sx={{ borderColor: "#333", my: 2 }} />

                        {/* Flash Quiz Push Button */}
                        <Box sx={{ mb: 3, p: 2, bgcolor: "#1a1a1a", borderRadius: 1, border: "1px solid #9C27B0" }}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                <FlashIcon sx={{ color: "#9C27B0", mr: 1 }} />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Flash Quiz
                                </Typography>
                            </Box>

                            {loadingFlashSession ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CircularProgress size={20} sx={{ color: "#4caf50" }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Loading...
                                    </Typography>
                                </Box>
                            ) : activeFlashSession ? (
                                <Box>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Active Session: <strong>{activeFlashSession.quiz_name}</strong>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Participants: {activeFlashSession.participant_count || 0}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                                        <Button
                                            variant="contained"
                                            startIcon={pushingQuestion ? <CircularProgress size={16} /> : <FlashIcon />}
                                            onClick={() => pushFlashQuestion(selectedSong?.songs_master_id)}
                                            disabled={pushingQuestion}
                                            sx={{
                                                bgcolor: "#9C27B0",
                                                "&:hover": { bgcolor: "#9C27B0" },
                                                textTransform: "none",
                                                fontWeight: 600
                                            }}
                                        >
                                            {pushingQuestion ? 'Pushing...' : 'Push'}
                                        </Button>

                                        {hasQuestions(selectedSong?.songs_master_id) && (
                                            <Button
                                                variant="outlined"
                                                startIcon={forcePushingQuestion === selectedSong?.songs_master_id ? <CircularProgress size={16} /> : <FastForwardIcon />}
                                                onClick={() => handleForceClick(selectedSong?.songs_master_id)}
                                                disabled={forcePushingQuestion === selectedSong?.songs_master_id}
                                                sx={{
                                                    borderColor: "#ff9800",
                                                    color: "#ff9800",
                                                    textTransform: "none",
                                                    fontWeight: 600,
                                                    "&:hover": {
                                                        borderColor: "#f57c00",
                                                        bgcolor: "rgba(255, 152, 0, 0.1)"
                                                    }
                                                }}
                                            >
                                                {forcePushingQuestion === selectedSong?.songs_master_id ? 'Pushing...' : 'Force'}
                                            </Button>
                                        )}

                                        <Tooltip title="Refresh session">
                                            <IconButton
                                                onClick={fetchActiveFlashSession}
                                                size="small"
                                                sx={{ color: "#4caf50" }}
                                            >
                                                <FilterList />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>

                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                        Delay is controlled by session config
                                    </Typography>
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No active flash quiz session. Create one from the Quiz Dashboard.
                                </Typography>
                            )}

                            {/* {hasQuestions(selectedSong?.songs_master_id) && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Quick quiz (no session required):
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={forcePushingQuestion === selectedSong?.songs_master_id ? <CircularProgress size={16} /> : <FastForwardIcon />}
                                        onClick={() => handleForceClick(selectedSong?.songs_master_id)}
                                        disabled={forcePushingQuestion === selectedSong?.songs_master_id}
                                        sx={{
                                            borderColor: "#ff9800",
                                            color: "#ff9800",
                                            textTransform: "none",
                                            "&:hover": {
                                                borderColor: "#f57c00",
                                                bgcolor: "rgba(255, 152, 0, 0.1)"
                                            }
                                        }}
                                    >
                                        {forcePushingQuestion === selectedSong?.songs_master_id ? 'Pushing...' : 'Force 1 Question'}
                                    </Button>
                                </Box>
                            )} */}
                        </Box>

                        <Divider sx={{ borderColor: "#333", my: 2 }} />

                        {/* YouTube Links */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                YouTube Links ({selectedSong?.urls?.length || 0})
                            </Typography>
                            {selectedSong?.urls?.length > 0 ? (
                                selectedSong.urls.map((url, i) => (
                                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                        <YouTube sx={{ color: "red" }} />
                                        <a
                                            href={url.song_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: "#1db954", wordBreak: "break-all" }}
                                        >
                                            {url.url_type_name || "YouTube Link"}
                                        </a>
                                    </Box>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No YouTube links available
                                </Typography>
                            )}
                        </Box>

                        {/* Song Files */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                Song Files ({selectedSong?.files?.length || 0})
                            </Typography>
                            {selectedSong?.files?.length > 0 ? (
                                selectedSong.files.map((file, i) => (
                                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                        <MusicNote sx={{ color: "skyblue" }} />
                                        <a
                                            href={`${process.env.REACT_APP_API_URL}/${file.file}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: "#1db954", wordBreak: "break-all" }}
                                        >
                                            {file.file_type_name || file.file.split("\\").pop()}
                                        </a>
                                    </Box>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No song files available
                                </Typography>
                            )}
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{ bgcolor: "#2c2c2c" }}>
                        <Button
                            onClick={() => setOpenSongModal(false)}
                            variant="contained"
                            sx={{ bgcolor: "#1db954", "&:hover": { bgcolor: "#1ed760" } }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Confirmation Dialog */}
                <Dialog
                    open={confirmDialogOpen}
                    onClose={() => {
                        setConfirmDialogOpen(false);
                        setPendingSong(null);
                    }}
                    PaperProps={{
                        sx: {
                            bgcolor: '#1e1e1e',
                            color: 'white',
                            borderRadius: '12px',
                            minWidth: '500px',
                            maxWidth: '600px'
                        }
                    }}
                >
                    <DialogTitle sx={{ pb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Confirm Push Question
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', mb: 2 }}>
                            {/* Song Card */}
                            <Box sx={{ flex: 1 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        p: 2,
                                        bgcolor: '#2a2a2a',
                                        borderRadius: '8px',
                                        border: '1px solid #333'
                                    }}
                                >
                                    {pendingSong?.image && (
                                        <img
                                            src={getImageUrl(pendingSong.image)}
                                            alt={pendingSong?.name || 'Song'}
                                            style={{
                                                width: 80,
                                                height: 80,
                                                borderRadius: 8,
                                                objectFit: 'cover'
                                            }}
                                        />
                                    )}
                                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: '#fff' }}>
                                            {pendingSong?.name || 'Unknown Song'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#999' }}>
                                            {pendingSong?.artist || 'Unknown Artist'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Timer Display */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '120px' }}>
                                {(() => {
                                    const timerState = getTimerState();
                                    if (!timerState) return null;

                                    return (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                p: 2,
                                                bgcolor: timerState.phase === 'delay' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                                                borderRadius: '8px',
                                                border: `1px solid ${timerState.phase === 'delay' ? '#4caf50' : '#f44336'}`,
                                                minWidth: '100px'
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ color: '#999', mb: 0.5 }}>
                                                {timerState.phase === 'delay' ? 'Delay' : 'Quiz Timer'}
                                            </Typography>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    color: timerState.phase === 'delay' ? '#4caf50' : '#f44336',
                                                    fontFamily: 'monospace'
                                                }}
                                            >
                                                {formatTimer(timerState.remainingSeconds)}
                                            </Typography>
                                        </Box>
                                    );
                                })()}
                            </Box>
                        </Box>

                        <Typography variant="body2" sx={{ color: '#999', mt: 2 }}>
                            {(() => {
                                const runningSongName = getCurrentRunningSongName();
                                const currentSongName = pendingSong?.name || 'this song';
                                return `${runningSongName} is currently running. Do you want to stop it and push ${currentSongName}?`;
                            })()}
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                        <Button
                            onClick={() => {
                                setConfirmDialogOpen(false);
                                setPendingSong(null);
                            }}
                            variant="outlined"
                            sx={{
                                color: '#fff',
                                borderColor: '#666',
                                '&:hover': {
                                    borderColor: '#888',
                                    bgcolor: 'rgba(255, 255, 255, 0.05)'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => handlePushConfirm()}
                            variant="contained"
                            disabled={pushingQuestion === pendingSong?.songs_master_id}
                            startIcon={pushingQuestion === pendingSong?.songs_master_id ? <CircularProgress size={16} /> : null}
                            sx={{
                                bgcolor: '#4caf50',
                                '&:hover': { bgcolor: '#45a049' },
                                minWidth: '100px'
                            }}
                        >
                            {pushingQuestion === pendingSong?.songs_master_id ? 'Pushing...' : 'Push'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Create Playlist Modal */}
                <CreatePlaylistModal
                    open={openCreatePlaylistModal}
                    onClose={() => setOpenCreatePlaylistModal(false)}
                    onSave={handleCreatePlaylist}
                />

                {/* Add Song Modal */}
                <AddSongModal
                    open={openAddSongModal}
                    onClose={() => {
                        setOpenAddSongModal(false);
                        setTargetSetIdForAdd(null);
                    }}
                    onSave={handleAddSongs}
                    playlistId={selectedPlaylist?.playlist_master_id}
                    targetSetId={targetSetIdForAdd}
                    availableSets={sets}
                    existingSongIds={songs.map(s => s.songs_master_id)}
                />
            </Box>
        );
    }

    // Show playlists table
    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ color: "white" }}>
                    Private Playlists ({privatePlaylists.length})
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setOpenCreatePlaylistModal(true)}
                    sx={{
                        bgcolor: '#9C27B0',
                        '&:hover': { bgcolor: '#7B1FA2' },
                    }}
                >
                    Create Private Playlist
                </Button>
            </Box>

            {privatePlaylists.length === 0 ? (
                <Typography sx={{ color: "white", textAlign: "center", mt: 4 }}>
                    No private playlists found
                </Typography>
            ) : (
                <TableContainer component={Paper} sx={{ bgcolor: "#1e1e1e" }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: "#2c2c2c" }}>
                                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Image</TableCell>
                                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Title</TableCell>
                                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Total Duration</TableCell>
                                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Songs Count</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {privatePlaylists.map((playlist) => (
                                <TableRow
                                    key={playlist.playlist_master_id}
                                    hover
                                    onClick={() => handlePlaylistClick(playlist)}
                                    sx={{
                                        cursor: "pointer",
                                        "&:hover": { bgcolor: "#282828" }
                                    }}
                                >
                                    <TableCell>
                                        {playlist.image ? (
                                            <img
                                                src={getImageUrl(playlist.image)}
                                                alt={playlist.title}
                                                style={{ width: 50, height: 50, borderRadius: 4, objectFit: "cover" }}
                                            />
                                        ) : (
                                            <Box sx={{ width: 50, height: 50, bgcolor: "#333", borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                <MusicNote sx={{ color: "#666" }} />
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ color: "white" }}>{playlist.title}</TableCell>
                                    <TableCell sx={{ color: "white" }}>{playlist.total_duration_text || "N/A"}</TableCell>
                                    <TableCell sx={{ color: "white" }}>
                                        <Chip label={playlist.songs_count || 0} color="primary" size="small" />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Create Playlist Modal */}
            <CreatePlaylistModal
                open={openCreatePlaylistModal}
                onClose={() => setOpenCreatePlaylistModal(false)}
                onSave={handleCreatePlaylist}
            />
        </Box>
    );
};

export default PrivatePlaylistTab;

